import { config } from "../config.js";
import { t } from "../i18n/index.js";
import { startWeaponName } from "./hangar.js";

// 目標(実績)。達成すると DOPA がもらえ、いくつかは「最初から持って出る武器」を解放する。
// 結果の画面では、まだの目標から近い順に「次の目標」を出す(もう一回遊ぶ理由にする)。
// check(run, save): run は1回の試合のまとめ(src/main.js の summarizeRun)。
// progress(run, save): 0〜1 の近さ(次の目標を選ぶ・バーを出すため)。
// text: 画面に出す名前の [キー, 値](src/i18n/)。名前は goalName(goal) で今の言語の文にする。

const stageFrom = (id) => config.stages.find((s) => s.id === id).from;
const reach = (id, reward, unlock) => ({
  id: `reach-${id}`,
  text: ["goal.reach", { place: `stage.${id}.place` }],
  reward,
  unlock,
  check: (r) => (r.areas ? r.areas.includes(id) : r.progress >= stageFrom(id)), // areas: その試合で通ったエリア(面によって時間が違う)
  progress: (r, s) => Math.max(r.progress, s.records.bestProgress) / stageFrom(id),
});
const count = (id, key, n, reward, unlock) => ({
  id,
  text: [`goal.${key}`, { n }],
  reward,
  unlock,
  check: (r) => r[key] >= n,
  progress: (r, s) => Math.max(r[key], s.records[`best${key[0].toUpperCase()}${key.slice(1)}`] || 0) / n,
});

export const GOALS = [
  reach("asteroid", 50, "spread"),
  count("kills-300", "kills", 300, 60),
  count("combo-50", "combo", 50, 80, "saber"),
  reach("colony", 100, "missile"),
  count("level-20", "level", 20, 100),
  reach("moon", 150),
  { id: "boss", text: ["goal.boss"], reward: 250, unlock: "mainGun", check: (r) => r.bossKilled, progress: (r, s) => Math.max(r.progress, s.records.bestProgress) / config.enemies.bossAt },
  count("kills-1000", "kills", 1000, 200),
  reach("atmosphere", 200),
  { id: "clear", text: ["goal.clear"], reward: 400, check: (r) => r.clear && r.mission >= 3, progress: (r, s) => Math.max(r.progress, s.records.bestProgress) / config.sim.runSeconds },
  count("combo-200", "combo", 200, 300),
  { id: "clear-norewind", text: ["goal.clear-norewind"], reward: 600, check: (r) => r.clear && r.mission >= 3 && r.rewinds === 0, progress: (r, s) => (s.records.clears ? 0.9 : 0.5 * (Math.max(r.progress, s.records.bestProgress) / config.sim.runSeconds)) },
  count("kills-2500", "kills", 2500, 500),
];

// 面ごとの目標(どの面にも同じ3つ。達成は save.stageGoals[面] に面ごとに残る)。
// ごほうびは config.stageGoals[id] × 面の dopa 倍率。run.holdsFailed: 時間切れで敵を残した停止戦の数(world.stats)
export const STAGE_GOALS = [
  { id: "clear", check: (r) => r.clear },
  { id: "norewind", check: (r) => r.clear && r.rewinds === 0 },
  { id: "allholds", check: (r) => r.clear && !r.holdsFailed },
];
export const stageGoalName = (goal) => t(`stagegoal.${goal.id}`);
export const stageGoalShort = (goal) => t(`stagegoal.short.${goal.id}`); // カードの小さな印の文
export const stageGoalReward = (goal, mul = 1) => Math.round(config.stageGoals[goal.id] * mul);
// その面の3つが全部できているか(カードを金色にする)
export const stagePerfect = (save, n) => STAGE_GOALS.every((g) => save.stageGoals[n]?.[g.id]);

// 目標の名前(今の言語)。値の中の place はステージ名のキーなので、それも文にする
export function goalName(goal) {
  const [key, params = {}] = goal.text;
  return t(key, params.place ? { ...params, place: t(params.place) } : params);
}

export function unlockText(goal) {
  return goal.unlock ? t("goal.unlock", { weapon: startWeaponName(goal.unlock) }) : "";
}

// 目標のごほうびの文(「60 DOPA」「50 DOPA + 武器」)
export function rewardText(goal) {
  return t(goal.unlock ? "goal.rewardWeapon" : "goal.reward", { n: goal.reward });
}

// 解放済みの「最初から持って出る武器」
export function unlockedWeapons(save) {
  return GOALS.filter((g) => g.unlock && save.goals[g.id]).map((g) => g.unlock);
}
